import React, { useMemo, useState, useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { useLanguage } from "@/contexts/LanguageContext";
import { getLightColor } from "@/utils/colorUtils";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import getProjectTranslation from "@/utils/getProjectTranslation";

interface CustomerContributionProps {
  transactions: any[];
  projectsDetails: any[];
  customerColor?: string;
}

interface TooltipPayload {
  name: string;
  value: number;
  dataKey: string;
}

const CustomerContribution: React.FC<CustomerContributionProps> = ({
  transactions,
  projectsDetails,
  customerColor = "#00383c", // Default fallback color
}) => {
  const { t, language } = useLanguage();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => {
      window.removeEventListener("resize", checkMobile);
    };
  }, []);

  // Calculate total contribution and money
  const { totalContribution } = useMemo(() => {
    return transactions.reduce(
      (acc, transaction) => {
        if (transaction.amount) {
          acc.totalContribution += transaction.amount;
          acc.totalMoney +=
            (transaction.amount || 0) * (transaction.price || 0);
        }
        return acc;
      },
      { totalContribution: 0, totalMoney: 0 },
    );
  }, [transactions]);

  // Prepare chart data
  const chartData = useMemo(() => {
    const projectData = new Map<
      string,
      {
        name: string;
        batches: Map<string, number>;
        total: number;
      }
    >();

    // Group transactions by project and batch
    transactions?.forEach((transaction) => {
      if (transaction.project?.id && transaction.batch?.title) {
        const project = projectData.get(transaction.project.id) || {
          name:
            getProjectTranslation(
              projectsDetails?.find((p) => p.id === transaction.project.id)
                ?.nameTranslations,
              language,
            ) ||
            projectsDetails?.find((p) => p.id === transaction.project.id)
              ?.title ||
            "Unknown Project",
          batches: new Map(),
          total: 0,
        };

        const batchAmount = (transaction.amount || 0) / 1000; // Convert to tons
        project.batches.set(
          transaction.batch.title,
          (project.batches.get(transaction.batch.title) || 0) + batchAmount,
        );
        project.total += batchAmount;
        projectData.set(transaction.project.id, project);
      }
    });

    // Convert to array format for Recharts
    return Array.from(projectData.values())
      .map((project) => ({
        name: project.name,
        ...Object.fromEntries(project.batches),
        total: project.total,
      }))
      .sort((a, b) => b.total - a.total); // Sort by total contribution
  }, [transactions, projectsDetails, language]);

  // Get unique batch names for stacked bars
  const batchNames = useMemo(() => {
    const names = new Set<string>();
    chartData.forEach((project) => {
      Object.keys(project).forEach((key) => {
        if (key !== "name" && key !== "total") {
          names.add(key);
        }
      });
    });
    return Array.from(names);
  }, [chartData]);

  // Generate colors for batches
  const batchColors = useMemo(() => {
    const colors: string[] = [];
    const baseColor = customerColor;
    const step = 0.3; // Color variation step

    for (let i = 0; i < batchNames.length; i++) {
      const opacity = 0.4 + i * step;
      colors.push(getLightColor(baseColor, opacity));
    }
    return colors;
  }, [batchNames, customerColor]);

  if (!transactions?.length) return null;

  // Generate lighter variants of the customer color for background
  const lightColorBackground = getLightColor(customerColor, 0.08); // 8% opacity
  const lighterColorBackground = getLightColor(customerColor, 0.15); // 15% opacity

  return (
    <div>
      <h2 className="text-2xl font-semibold mb-2 text-gray-800">
        {t("dashboard.contribution.title")}
      </h2>
      <p className="text-gray-600 mb-6">
        {t("dashboard.contribution.subtitle")}
      </p>
      <Card className="border border-gray-200 rounded-t-lg">
        <CardHeader
          className="px-6 py-4 rounded-t-lg"
          style={{
            background: `linear-gradient(to right, ${lightColorBackground}, ${lighterColorBackground})`,
          }}
        >
          <div className="flex items-center justify-between">
            <p className="text-lg font-medium text-gray-700">
              {t("dashboard.contribution.totalSupport")}
            </p>
            <div className="flex flex-col items-end gap-1">
              <span
                className="text-2xl font-bold"
                style={{ color: customerColor }}
              >
                {new Intl.NumberFormat(language === "en" ? "en-US" : "de-DE", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }).format(totalContribution / 1000)}{" "}
                t CO₂e
              </span>
            </div>
          </div>
        </CardHeader>
        <CardContent className="px-1 py-4 md:px-6">
          <div className="h-[400px] md:h-[500px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                margin={{ top: 20, right: 30, left: 0, bottom: 60 }}
                layout="vertical"
              >
                <XAxis
                  type="number"
                  tickFormatter={(value) => `${value} t CO₂e`}
                  label={{
                    value: t("dashboard.contribution.co2Reduction"),
                    position: "bottom",
                    offset: 10,
                  }}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={150}
                  tick={{ fontSize: 12 }}
                  interval={0}
                  tickFormatter={(value) => {
                    // Truncate long project names
                    return value.length > 20
                      ? `${value.substring(0, 20)}...`
                      : value;
                  }}
                />
                <Tooltip
                  cursor={false}
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      const total = (payload as TooltipPayload[]).reduce(
                        (sum, item) => sum + item.value,
                        0,
                      );
                      return (
                        <div className="bg-white p-3 border rounded shadow-sm">
                          <p className="font-medium mb-2">{label}</p>
                          {(payload as TooltipPayload[]).map((item, index) => (
                            <p key={index} className="text-sm">
                              {item.name}: {item.value.toFixed(2)} t CO₂e
                            </p>
                          ))}
                          <p className="text-sm font-medium mt-2">
                            {t("dashboard.contribution.total")}:{" "}
                            {total.toFixed(2)} t CO₂e
                          </p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                {batchNames.map((batchName, index) => (
                  <Bar
                    key={batchName}
                    dataKey={batchName}
                    stackId="a"
                    fill={batchColors[index]}
                    name={batchName}
                    barSize={30}
                    radius={
                      index === batchNames.length - 1 ? [0, 4, 4, 0] : undefined
                    }
                  />
                ))}
                <Legend
                  formatter={(value) => (
                    <span className="text-gray-700 text-sm">{value}</span>
                  )}
                  layout={isMobile ? "horizontal" : "vertical"}
                  align={isMobile ? "center" : "right"}
                  verticalAlign={isMobile ? "bottom" : "middle"}
                  wrapperStyle={{
                    paddingTop: "40px",
                  }}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CustomerContribution;
